"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faMagnifyingGlass,
  faSpinner,
  faXmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

import { Button } from "@/components/ui/button/Button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "@/components/ui/dialog/Dialog";
import FormInput from "@/components/ui/form-input";
import { CC_SEARCH } from "@/components/case-checking/caseCheckingChrome";
import { CaseNewCaseFileUpload } from "@/components/case-checking/CaseNewCaseFileUpload";
import { useCaseNewCaseFileUpload } from "@/components/case-checking/useCaseNewCaseFileUpload";
import {
  launchProgenyCaseCheck,
  lookupProgenyAccount,
} from "@/services/api/case-checking";
import { useAuth } from "@/context/AuthContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { caseCheckAgentName } from "./agenticCaseCheck";
import { parseCuroAccountId } from "./progenyCuroId";

type LaunchMode = "progeny_pull" | "manual";

export function ProgenyNewCaseDialog({
  onClose,
  onCaseLaunched,
  companyId,
}: {
  onClose: () => void;
  onCaseLaunched: (caseId: string) => void;
  companyId?: string;
}) {
  const agentName = caseCheckAgentName(companyId);
  const isAdmin = useIsAdmin();
  const { user } = useAuth();
  const showManualMarvin =
    isAdmin || Boolean(user?.email?.toLowerCase().endsWith("@ani.tech"));

  const [mode, setMode] = useState<LaunchMode>("progeny_pull");
  const [curoInput, setCuroInput] = useState("");
  const [resolvedAccountId, setResolvedAccountId] = useState<string | null>(null);
  const [resolvedName, setResolvedName] = useState<string | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [launchLoading, setLaunchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const manualUpload = useCaseNewCaseFileUpload({
    companyId,
    flowKind: "case_check_mission",
  });

  const parsedAccountId = useMemo(() => parseCuroAccountId(curoInput), [curoInput]);

  const resetLookup = useCallback(() => {
    setResolvedAccountId(null);
    setResolvedName(null);
    setLookupError(null);
  }, []);

  useEffect(() => {
    resetLookup();
  }, [curoInput, resetLookup]);

  useEffect(() => {
    setError(null);
    manualUpload.setError(null);
  }, [mode, manualUpload.setError]);

  const handleLookup = async () => {
    setLookupError(null);
    if (!parsedAccountId) {
      setLookupError("Enter a valid Curo account ID or paste the full CRM link.");
      return;
    }
    setLookupLoading(true);
    try {
      const result = await lookupProgenyAccount(parsedAccountId);
      setResolvedAccountId(result.account_id);
      setResolvedName(result.name);
      if (!result.name) {
        setLookupError("Account found but has no name in Progeny — check the ID is correct.");
      }
    } catch (err) {
      resetLookup();
      setLookupError(
        err instanceof Error ? err.message : "Could not find that account in Progeny.",
      );
    } finally {
      setLookupLoading(false);
    }
  };

  const handleLaunchProgenyPull = async () => {
    setError(null);
    const accountId = resolvedAccountId ?? parsedAccountId;
    if (!accountId) {
      setError("Enter a valid Curo account ID first.");
      return;
    }
    if (!resolvedName && !resolvedAccountId) {
      setError("Look up the account first to confirm the client name.");
      return;
    }

    setLaunchLoading(true);
    try {
      const result = await launchProgenyCaseCheck({
        progeny_account_id: accountId,
        client_name: resolvedName ?? undefined,
      });
      onCaseLaunched(result.case_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start the file check.");
    } finally {
      setLaunchLoading(false);
    }
  };

  const handleLaunchManual = async () => {
    setError(null);
    const caseId = await manualUpload.submit();
    if (caseId) {
      onCaseLaunched(caseId);
    }
  };

  const busy = lookupLoading || launchLoading || manualUpload.isProcessing;
  const manualError = error ?? manualUpload.error;

  return (
    <Dialog open onClose={onClose} size="2xl">
      <div className="border-b border-slate-200 dark:border-slate-700">
        <DialogTitle>
          <div className="flex items-center justify-between gap-3">
            <span className="text-lg font-medium text-slate-900 dark:text-white">New case</span>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-lg p-2 text-slate-500 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 dark:hover:bg-slate-700"
              aria-label="Close dialog"
            >
              <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
            </button>
          </div>
        </DialogTitle>
      </div>

      <DialogBody className="p-6 pt-6 overflow-y-auto flex-1 space-y-4">
        {showManualMarvin ? (
          <div
            className="flex gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-1"
            data-testid="progeny-new-case-mode-tabs"
          >
            <Button
              plain
              size="unstyled"
              type="button"
              onClick={() => setMode("progeny_pull")}
              className={clsx(
                "flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                mode === "progeny_pull"
                  ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                  : "text-slate-600 dark:text-slate-400",
              )}
            >
              From Curo ID
            </Button>
            <Button
              plain
              size="unstyled"
              type="button"
              onClick={() => setMode("manual")}
              className={clsx(
                "flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                mode === "manual"
                  ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                  : "text-slate-600 dark:text-slate-400",
              )}
            >
              Manual ({agentName})
            </Button>
          </div>
        ) : null}

        {manualError && mode !== "manual" ? (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-700 dark:text-red-300 text-sm">{manualError}</p>
          </div>
        ) : null}

        {mode === "manual" && showManualMarvin ? (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Upload advice files here. After intake, {agentName} runs triage, assessment, and
              review automatically — you will land on the case detail view.
            </p>
            <CaseNewCaseFileUpload
              files={manualUpload.files}
              isProcessing={manualUpload.isProcessing}
              processingStep={manualUpload.processingStep}
              fileInputRef={manualUpload.fileInputRef}
              onFileSelect={manualUpload.handleFileSelect}
              onPickFiles={() => manualUpload.fileInputRef.current?.click()}
              error={manualError}
              testId="progeny-manual-file-upload"
            />
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Paste the Curo account ID from the CRM (or the full account link). We pull the advice
              file from Progeny and run the file check in the background — usually 5–15 minutes.
            </p>

            <div className="space-y-2">
              <label
                htmlFor="progeny-curo-id"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                Curo account ID
              </label>
              <div className="flex gap-2">
                <FormInput
                  id="progeny-curo-id"
                  data-testid="progeny-curo-id-input"
                  value={curoInput}
                  onChange={(e) => setCuroInput(e.target.value)}
                  placeholder="e.g. c6bfd3b1-ebae-ee11-a569-0022481abc8f"
                  disabled={busy}
                  className={clsx("flex-1", CC_SEARCH)}
                />
                <Button
                  type="button"
                  plain
                  onClick={() => void handleLookup()}
                  disabled={busy || !parsedAccountId}
                  data-testid="progeny-curo-lookup"
                  className="shrink-0"
                  icon={
                    lookupLoading ? (
                      <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                    ) : (
                      <FontAwesomeIcon icon={faMagnifyingGlass} className="w-4 h-4" />
                    )
                  }
                >
                  Look up
                </Button>
              </div>
              {parsedAccountId && !resolvedAccountId ? (
                <p className="text-xs text-slate-500">Recognised account ID — look up to confirm client name.</p>
              ) : null}
            </div>

            {lookupError ? (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-amber-800 dark:text-amber-200 text-sm">{lookupError}</p>
              </div>
            ) : null}

            {resolvedAccountId && resolvedName ? (
              <div
                className="flex items-start gap-3 p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5"
                data-testid="progeny-curo-lookup-result"
              >
                <FontAwesomeIcon icon={faCheck} className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">{resolvedName}</p>
                  <p className="text-xs text-slate-500 mt-1 font-mono">{resolvedAccountId}</p>
                  <p className="text-xs text-slate-500 mt-2">
                    Confirm this is the right client, then start the file check.
                  </p>
                </div>
              </div>
            ) : null}
          </>
        )}
      </DialogBody>

      <div className="border-t border-slate-200 dark:border-slate-700">
        <DialogActions>
          <div className="w-full">
            {mode === "manual" && showManualMarvin ? (
              <Button
                type="button"
                color="indigo"
                onClick={() => void handleLaunchManual()}
                disabled={busy || !manualUpload.hasFiles}
                className="w-full py-3 !rounded-xl"
                data-testid="progeny-manual-start-file-check"
                icon={
                  manualUpload.isProcessing ? (
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                  ) : undefined
                }
              >
                {manualUpload.isProcessing ? "Starting…" : "Start file check"}
              </Button>
            ) : (
              <Button
                type="button"
                color="indigo"
                onClick={() => void handleLaunchProgenyPull()}
                disabled={busy || !resolvedAccountId || !resolvedName}
                className="w-full py-3 !rounded-xl"
                data-testid="progeny-launch-case-check"
                icon={
                  launchLoading ? (
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                  ) : undefined
                }
              >
                {launchLoading ? "Starting file check…" : "Start file check"}
              </Button>
            )}
          </div>
        </DialogActions>
      </div>
    </Dialog>
  );
}
